import { GitCommit } from 'lucide-react';

import { Icon } from '@@/Icon';
import { Badge } from '@@/Badge';
import { ResourceDetailHeader } from '@@/ResourceDetailHeader/ResourceDetailHeader';
import { HeaderStats } from '@@/ResourceDetailHeader/HeaderStats';
import { ResourceStatBlock } from '@@/ResourceDetailHeader/ResourceStatBlock';
import { ActionBarShell } from '@@/ResourceDetailHeader/ActionBarShell';

import { Workflow } from '../types';
import { StatusBadge } from '../../components/StatusBadge';
import { effectiveWorkflowStatus, computeTargetRollup } from '../status';
import { MixedArtifactObject } from '../queries/useArtifactRealObjects';
import { ReconcileButton } from '../ReconcileButton';

import { WorkflowDeleteButton } from './WorkflowDeleteButton';
import { EditButton } from './EditButton';

interface Props {
  workflow: Workflow;
  artifactObjects: MixedArtifactObject[];
}

export function WorkflowResourceHeader({ workflow, artifactObjects }: Props) {
  const status = effectiveWorkflowStatus(workflow);
  const rollup = computeTargetRollup(workflow);

  const releasesCharts = workflow.artifacts.some((artifact) => !!artifact.helm);

  return (
    <ResourceDetailHeader
      icon={<Icon icon={GitCommit} size="xl" />}
      title={workflow.name}
      badge={
        <div className="flex items-center gap-2">
          <StatusBadge status={status.status} />
          {releasesCharts && <Badge type="info">Helm</Badge>}
        </div>
      }
      rightInfo={
        <HeaderStats>
          <ResourceStatBlock
            status={rollup.tone}
            data-cy="workflow-targets-stat"
          >
            <ResourceStatBlock.Label>Targets</ResourceStatBlock.Label>
            <ResourceStatBlock.Value align="center" size="base" dot>
              {rollup.total > 0
                ? `${rollup.synced}/${rollup.total} synced`
                : 'No artifacts'}
            </ResourceStatBlock.Value>
          </ResourceStatBlock>
        </HeaderStats>
      }
      actionBar={
        <ActionBarShell>
          <div className="flex items-center gap-2">
            <EditButton workflow={workflow} artifactObjects={artifactObjects} />
            <ReconcileButton workflow={workflow} />
          </div>
          <div className="ml-auto">
            <WorkflowDeleteButton workflow={workflow} />
          </div>
        </ActionBarShell>
      }
    />
  );
}
